import { useState, useRef } from "react";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UploadSimple, X, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import PersonSilhouette from "./PersonSilhouette";

const ROLES_BY_TYPE = {
  comunal: ["alcalde", "concejal", "funcionario"],
  gobierno: ["delegado_presidencial", "seremi", "parlamentario"],
  gore: ["gobernador", "jefe_unidad"],
  organizacion: ["presidente", "directorio", "representante"],
};

const SUGGESTED_AREAS = [
  "Animalista", "Deporte", "Música", "Cultura", "Medio ambiente",
  "Educación", "Salud", "Adulto mayor", "Mujer", "Pueblos originarios",
  "Emprendimiento", "Vivienda", "Turismo", "Patrimonio",
];

function authImageUrl(foto_url) {
  if (!foto_url) return null;
  // Si es URL externa (ImgBB / cualquier https), devolverla tal cual
  if (foto_url.startsWith("http://") || foto_url.startsWith("https://")) {
    return foto_url;
  }
  // URL del backend de documents protegida — agregar token via query
  if (foto_url.startsWith("/api/files/")) {
    const token = localStorage.getItem("sigsaop_token");
    return `${process.env.REACT_APP_BACKEND_URL}${foto_url}?auth=${token}`;
  }
  return foto_url;
}

export function PhotoUploader({ value, onChange, size = 96 }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => ref.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Selecciona una imagen");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("related_entity_type", "authority_member_photo");
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // public_url: URL externa (ImgBB) si es imagen; proxy autenticado para otros
      onChange(data.public_url || `/api/files/${data.id}/download`);
      toast.success("Foto subida");
    } catch {
      toast.error("No se pudo subir la foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = () => onChange("");
  const preview = authImageUrl(value);

  return (
    <div className="flex items-center gap-3">
      <div
        className="relative rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0"
        style={{ width: size, height: size }}
        data-testid="member-photo-preview"
      >
        {preview ? (
          <img src={preview} alt="" className="w-full h-full object-cover" />
        ) : (
          <PersonSilhouette size={size} />
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="member-photo-input" />
        <Button type="button" size="sm" variant="outline" onClick={handlePick} disabled={uploading} className="gap-2" data-testid="member-photo-upload-btn">
          <UploadSimple size={14} />
          {uploading ? "Subiendo…" : (value ? "Cambiar foto" : "Subir foto")}
        </Button>
        {value && (
          <button type="button" onClick={remove} className="text-xs text-red-600 hover:underline text-left" data-testid="member-photo-remove-btn">
            Quitar foto
          </button>
        )}
      </div>
    </div>
  );
}

export function AreasInteresInput({ value = [], onChange }) {
  const [input, setInput] = useState("");

  const add = (raw) => {
    const v = (raw ?? input).trim();
    if (!v) return;
    if (value.includes(v)) {
      setInput("");
      return;
    }
    onChange([...value, v]);
    setInput("");
  };

  const remove = (i) => {
    const out = [...value]; out.splice(i, 1); onChange(out);
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    } else if (e.key === "Backspace" && !input && value.length > 0) {
      remove(value.length - 1);
    }
  };

  const remainingSuggested = SUGGESTED_AREAS.filter(s => !value.includes(s));

  return (
    <div className="space-y-2" data-testid="areas-interes-input">
      <div className="flex flex-wrap gap-1.5 min-h-[2.5rem] border border-slate-200 rounded-md px-2 py-1.5 bg-white">
        {value.map((tag, i) => (
          <span key={i} data-testid={`area-chip-${i}`} className="inline-flex items-center gap-1 bg-slate-900 text-white text-xs px-2 py-1 rounded-full">
            {tag}
            <button type="button" onClick={() => remove(i)} className="hover:text-red-300" data-testid={`remove-area-${i}`}>
              <X size={11} weight="bold" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          onBlur={() => input.trim() && add()}
          placeholder={value.length === 0 ? "Animalista, Rock, Deporte…" : ""}
          className="flex-1 outline-none text-sm min-w-[120px] bg-transparent"
          data-testid="area-input-text"
        />
      </div>
      {remainingSuggested.length > 0 && (
        <div className="flex flex-wrap gap-1">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 self-center mr-1">Sugerencias:</span>
          {remainingSuggested.slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200"
              data-testid={`suggest-area-${s.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Plus size={10} className="inline mr-0.5" /> {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function memberFormDefault() {
  return {
    role: "", name: "", adhesion_politica: "", profesion: "",
    email: "", telefono: "", foto_url: "", empresa: "", cargo: "",
    areas_interes: [],
  };
}

export function MemberForm({ form, setForm, entityType }) {
  return (
    <div className="space-y-4">
      <PhotoUploader value={form.foto_url} onChange={(v) => setForm({ ...form, foto_url: v })} />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Nombre *</Label>
          <Input
            data-testid="member-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <Label>Cargo / Rol *</Label>
          <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
            <SelectTrigger data-testid="member-role-select"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>
              {(ROLES_BY_TYPE[entityType] || []).map((r) => (
                <SelectItem key={r} value={r}>{r.replace("_", " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Adhesión política / Sector</Label>
          <Input
            data-testid="member-adhesion-input"
            value={form.adhesion_politica}
            onChange={(e) => setForm({ ...form, adhesion_politica: e.target.value })}
            placeholder="DC, FA, PS, Indep., …"
          />
        </div>
        <div>
          <Label>Profesión</Label>
          <Input
            data-testid="member-profesion-input"
            value={form.profesion}
            onChange={(e) => setForm({ ...form, profesion: e.target.value })}
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            type="email"
            data-testid="member-email-input"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <div>
          <Label>Teléfono</Label>
          <Input
            data-testid="member-phone-input"
            value={form.telefono}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })}
          />
        </div>
        {entityType === "organizacion" && (
          <>
            <div>
              <Label>Empresa</Label>
              <Input
                data-testid="member-empresa-input"
                value={form.empresa}
                onChange={(e) => setForm({ ...form, empresa: e.target.value })}
              />
            </div>
            <div>
              <Label>Cargo en empresa</Label>
              <Input
                data-testid="member-empresa-cargo-input"
                value={form.cargo}
                onChange={(e) => setForm({ ...form, cargo: e.target.value })}
              />
            </div>
          </>
        )}
      </div>

      <div>
        <Label>Áreas de interés</Label>
        <p className="text-xs text-slate-500 mb-1.5">
          Presiona Enter o coma para agregar. Útil para identificar afinidades con autoridades.
        </p>
        <AreasInteresInput
          value={form.areas_interes || []}
          onChange={(v) => setForm({ ...form, areas_interes: v })}
        />
      </div>
    </div>
  );
}

export { authImageUrl };
